import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryIcon } from '../components/CategoryIcon';
import { Dialogue } from '../components/Dialogue';
import { GoalRow } from '../components/GoalRow';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { useAddQuest } from '../lib/addQuest';
import { prettyDate, prettyDay } from '../lib/date';
import { useStore } from '../lib/store';
import { currentStreak, hasCheckIn } from '../lib/streak';
import { FALLBACK_CAT, type Goal, type GoalType } from '../lib/types';

// 'tended' is a pseudo-filter: rather than narrowing the backlog, it swaps the
// main panel over to the finished-errands view. (It's already covered by the
// `string` arm — listed here for documentation.)
type Filter = 'all' | 'tended' | GoalType | string;

export function QuestsScreen() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const dueParam = params.get('due');
  const { openModal } = useAddQuest();
  const { goals, categories, categoryMap, checkInIndex, today, toggleToday, updateGoal } = useStore();
  const [filter, setFilter] = useState<Filter>('all');
  const viewingTended = filter === 'tended';

  // Tended (completed) errands are kept out of the backlog so they don't clog
  // it; they're viewed via the TENDED rail item instead. Habits never carry
  // completed_at, so they're untouched.
  const all = goals.filter(g => !g.archived_at && !g.completed_at);
  const filtered = useMemo(() => {
    let list = all;
    if (filter === 'habit' || filter === 'todo') {
      list = list.filter(g => g.type === filter);
    } else if (filter !== 'all' && filter !== 'tended') {
      list = list.filter(g => g.cat === filter);
    }
    if (dueParam) {
      list = list.filter(g => g.type === 'todo' && g.due_date === dueParam);
    }
    return list;
  }, [all, filter, dueParam]);

  // Finished errands, newest-first. ISO timestamps sort lexically in
  // chronological order, so the freshest finishes sit at the top.
  const tended = useMemo(
    () =>
      goals
        .filter(g => g.type === 'todo' && g.completed_at && !g.archived_at)
        .sort((a, b) => b.completed_at!.localeCompare(a.completed_at!)),
    [goals],
  );

  const filterKeys: string[] = categories.map(c => c.key);

  const now = new Date();

  const habitsList = filtered.filter(g => g.type === 'habit');
  const todosList = filtered.filter(g => g.type === 'todo');
  const todoGroups = useMemo(() => groupTodosByDate(todosList, today), [todosList, today]);

  const clearDue = () => {
    const next = new URLSearchParams(params);
    next.delete('due');
    setParams(next);
  };

  const clearAllTended = async () => {
    if (
      !confirm(
        `Tuck all ${tended.length} tended errand${
          tended.length === 1 ? '' : 's'
        } into the archives? They'll rest quietly out of sight.`,
      )
    ) {
      return;
    }
    await Promise.all(tended.map(g => updateGoal(g.id, { archive: true })));
  };

  return (
    <div className="grid-quests">
      <Panel title="FILTER" style={{ alignSelf: 'start' }} bodyClassName="panel-body filter-rail">
        {[
          { k: 'all' as const, label: 'ALL CHORES' },
          { k: 'habit' as const, label: 'DAILY HABITS' },
          { k: 'todo' as const, label: 'ONE-OFF ERRANDS' },
        ].map(f => (
          <FilterRow key={f.k} active={filter === f.k} onClick={() => setFilter(f.k)}>
            {f.label}
          </FilterRow>
        ))}
        <div className="section-label" style={{ margin: '12px 0 6px' }}>— CATEGORY —</div>
        {filterKeys.map(k => {
          const meta = categoryMap.get(k) ?? FALLBACK_CAT;
          return (
            <FilterRow
              key={k}
              active={filter === k}
              onClick={() => setFilter(k)}
              dot={meta.color}
              icon={meta.icon}
            >
              {meta.label}
            </FilterRow>
          );
        })}
        <div className="section-label" style={{ margin: '12px 0 6px' }}>— DONE —</div>
        <FilterRow active={viewingTended} onClick={() => setFilter('tended')}>
          TENDED{tended.length > 0 ? ` · ${tended.length}` : ''}
        </FilterRow>
      </Panel>

      {viewingTended ? (
        <Panel title={`TENDED · ${tended.length} AT REST`} bodyClassName="panel-body">
          {tended.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b3a1d', fontFamily: 'VT323', fontSize: 20 }}>
              Nothing at rest yet. ▸ tick off an errand and it'll come here to rest.
            </div>
          ) : (
            <>
              <Dialogue speaker="NOTEBOOK" style={{ marginBottom: 16 }}>
                A tidy pile of finished errands. Untick one to bring it back to your list, friend.
              </Dialogue>
              {tended.map(g => (
                <GoalRow
                  key={g.id}
                  goal={g}
                  done
                  streak={0}
                  subtitle={`tended ${prettyDate(g.completed_at!)}`}
                  onToggle={() => toggleToday(g)}
                  onOpen={() => nav(`/quest/${g.id}`)}
                />
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <PixelButton variant="ghost" onClick={clearAllTended}>CLEAR ALL</PixelButton>
              </div>
            </>
          )}
        </Panel>
      ) : (
        <Panel title={`CHORES · ${filtered.length} FOUND`} bodyClassName="panel-body">
          {dueParam && (
            <button
              type="button"
              className="quests-date-chip"
              onClick={clearDue}
              aria-label={`clear date filter for ${dueParam}`}
            >
              DUE: {prettyDay(dueParam).toUpperCase()} <span aria-hidden="true">×</span>
            </button>
          )}

          {filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b3a1d', fontFamily: 'VT323', fontSize: 20 }}>
              Nothing matches here. ▸ try another filter.
            </div>
          )}

          {dueParam
            ? todosList.map(g => (
                <GoalRow
                  key={g.id}
                  goal={g}
                  done={!!g.completed_at}
                  streak={0}
                  onToggle={() => toggleToday(g)}
                  onOpen={() => nav(`/quest/${g.id}`)}
                />
              ))
            : (
              <>
                {habitsList.length > 0 && (
                  <>
                    {todoGroups.length > 0 && (
                      <div className="section-label" style={{ margin: '4px 0 8px' }}>— DAILY HABITS —</div>
                    )}
                    {habitsList.map(g => (
                      <GoalRow
                        key={g.id}
                        goal={g}
                        done={hasCheckIn(checkInIndex, g.id, today)}
                        streak={currentStreak(checkInIndex, g.id, now)}
                        onToggle={() => toggleToday(g)}
                        onOpen={() => nav(`/quest/${g.id}`)}
                      />
                    ))}
                  </>
                )}

                {todoGroups.map(group => (
                  <div key={group.key}>
                    <div className="section-label" style={{ margin: '14px 0 8px' }}>— {group.label} —</div>
                    {group.items.map(g => (
                      <GoalRow
                        key={g.id}
                        goal={g}
                        done={!!g.completed_at}
                        streak={0}
                        onToggle={() => toggleToday(g)}
                        onOpen={() => nav(`/quest/${g.id}`)}
                      />
                    ))}
                  </div>
                ))}
              </>
            )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <PixelButton variant="gold" onClick={openModal}>+ NEW CHORE</PixelButton>
            <PixelButton onClick={() => nav('/stats')}>STATS</PixelButton>
          </div>
        </Panel>
      )}
    </div>
  );
}

interface TodoGroup {
  key: string;
  label: string;
  items: Goal[];
}

function groupTodosByDate(todos: Goal[], today: string): TodoGroup[] {
  const overdue: Goal[] = [];
  const todayItems: Goal[] = [];
  const unscheduled: Goal[] = [];
  const futureByDay = new Map<string, Goal[]>();

  for (const g of todos) {
    if (!g.due_date) unscheduled.push(g);
    else if (g.due_date < today) overdue.push(g);
    else if (g.due_date === today) todayItems.push(g);
    else {
      const bucket = futureByDay.get(g.due_date) ?? [];
      bucket.push(g);
      futureByDay.set(g.due_date, bucket);
    }
  }

  const groups: TodoGroup[] = [];
  if (overdue.length) groups.push({ key: 'overdue', label: 'OVERDUE', items: overdue });
  if (todayItems.length) groups.push({ key: 'today', label: 'TODAY', items: todayItems });

  const futureKeys = [...futureByDay.keys()].sort();
  for (const k of futureKeys) {
    groups.push({
      key: k,
      label: prettyDay(k).toUpperCase(),
      items: futureByDay.get(k)!,
    });
  }

  if (unscheduled.length) groups.push({ key: 'unscheduled', label: 'UNSCHEDULED', items: unscheduled });
  return groups;
}

function FilterRow({
  children,
  active,
  onClick,
  dot,
  icon,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  dot?: string;
  icon?: string | null;
}) {
  return (
    <div
      className={`filter-row ${active ? 'filter-row--active' : ''}`}
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className="cursor" style={{ visibility: active ? 'visible' : 'hidden' }} />
      {icon ? <CategoryIcon slug={icon} size={18} alt="" /> : dot && <span className="dot" style={{ background: dot }} />}
      {children}
    </div>
  );
}
